import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import axios from '../../common/axios';

type Option = { guid: string; title: string };
export type FlashcardCreationState = {
  pathways: Option[];
  topics: Option[];
  pathway: Option | null;
  topic: Option | null;
  pathwaySearch: string;
  front: string;
  back: string;
  loadingPathways: boolean;
  loadingTopics: boolean;
  saving: boolean;
  pathwayError: string;
  topicError: string;
  saveError: string;
  pathwayRequestId?: string;
  topicRequestId?: string;
  saveRequestId?: string;
};
const initialState: FlashcardCreationState = {
  pathways: [],
  topics: [],
  pathway: null,
  topic: null,
  pathwaySearch: '',
  front: '',
  back: '',
  loadingPathways: false,
  loadingTopics: false,
  saving: false,
  pathwayError: '',
  topicError: '',
  saveError: ''
};

export const fetchCreationPathways = createAsyncThunk(
  'flashcardCreation/pathways',
  async (search: string, { signal }) => {
    const { data } = await axios.get('pathways/me', {
      params: { page: 0, pageSize: 50, type: 'owned', searchTerm: search },
      signal
    });
    return (data.items ?? []) as Option[];
  }
);

export const fetchCreationTopics = createAsyncThunk(
  'flashcardCreation/topics',
  async (pathwayGuid: string, { signal }) => {
    const options: Option[] = [];
    let index = 0;
    while (!signal.aborted) {
      const { data } = await axios.get(`pathways/${pathwayGuid}/topics`, {
        params: { index, step: 100 },
        signal
      });
      const items: Option[] = data.items ?? [];
      options.push(...items);
      if (items.length < 100) break;
      index += 1;
    }
    return options;
  }
);

export const createFlashcard = createAsyncThunk<
  void,
  void,
  { state: { flashcardCreation: FlashcardCreationState } }
>(
  'flashcardCreation/save',
  async (_, { getState }) => {
    const { topic, front, back } = getState().flashcardCreation;
    if (!topic) throw new Error('A topic is required');
    await axios.post(`topics/${topic.guid}/flashcards`, {
      front: front.trim(),
      back: back.trim()
    });
  },
  {
    condition: (_, { getState }) => {
      const { saving, topic, front, back } = getState().flashcardCreation;
      return !saving && !!topic && !!front.trim() && !!back.trim();
    }
  }
);

export const creationSlice = createSlice({
  name: 'flashcardCreation',
  initialState,
  reducers: {
    resetCreation: () => initialState,
    setPathwaySearch(state, action: PayloadAction<string>) {
      state.pathwaySearch = action.payload;
    },
    selectPathway(state, action: PayloadAction<Option | null>) {
      state.pathway = action.payload;
      state.topic = null;
      state.topics = [];
      state.topicRequestId = undefined;
      state.loadingTopics = false;
      state.topicError = '';
      state.saveError = '';
    },
    selectTopic(state, action: PayloadAction<Option | null>) {
      state.topic = action.payload;
    },
    setFront(state, action: PayloadAction<string>) {
      state.front = action.payload;
    },
    setBack(state, action: PayloadAction<string>) {
      state.back = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreationPathways.pending, (state, action) => {
        state.pathwayRequestId = action.meta.requestId;
        state.loadingPathways = true;
        state.pathwayError = '';
      })
      .addCase(fetchCreationPathways.fulfilled, (state, action) => {
        if (state.pathwayRequestId !== action.meta.requestId) return;
        state.pathways = action.payload;
        state.loadingPathways = false;
        state.pathwayRequestId = undefined;
      })
      .addCase(fetchCreationPathways.rejected, (state, action) => {
        if (state.pathwayRequestId !== action.meta.requestId) return;
        state.loadingPathways = false;
        state.pathwayRequestId = undefined;
        if (!action.meta.aborted) state.pathwayError = 'flashcard_review.load_options_error';
      })
      .addCase(fetchCreationTopics.pending, (state, action) => {
        state.topicRequestId = action.meta.requestId;
        state.loadingTopics = true;
        state.topicError = '';
      })
      .addCase(fetchCreationTopics.fulfilled, (state, action) => {
        if (state.topicRequestId !== action.meta.requestId) return;
        state.topics = action.payload;
        state.loadingTopics = false;
        state.topicRequestId = undefined;
      })
      .addCase(fetchCreationTopics.rejected, (state, action) => {
        if (state.topicRequestId !== action.meta.requestId) return;
        state.loadingTopics = false;
        state.topicRequestId = undefined;
        if (!action.meta.aborted) state.topicError = 'flashcard_review.load_options_error';
      })
      .addCase(createFlashcard.pending, (state, action) => {
        state.saving = true;
        state.saveRequestId = action.meta.requestId;
        state.saveError = '';
      })
      .addCase(createFlashcard.fulfilled, (state, action) => {
        if (state.saveRequestId !== action.meta.requestId) return;
        state.saving = false;
        state.saveRequestId = undefined;
      })
      .addCase(createFlashcard.rejected, (state, action) => {
        if (state.saveRequestId !== action.meta.requestId) return;
        state.saving = false;
        state.saveRequestId = undefined;
        state.saveError = 'flashcard_review.create_error';
      });
  }
});
export default creationSlice.reducer;
